import React, { useEffect, useState } from "react";
import {
  AddMenuMap,
  getAllMenulist,
  getAllPagesList,
} from "services/apiService";
import { Page } from "types";
import {
  Modal,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Stack,
  Grid,
  Paper,
} from "@mui/material";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const MenuMap = ({ open, handleClose, onActionComplete }) => {
  const [menusList, setMenusList] = useState([]);
  const [pagesListData, setPagesListData] = useState<Page[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState<number | null>(null);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchMenusAndPages();
    }
  }, [open]);

  const fetchMenusAndPages = async () => {
    setLoading(true);
    try {
      const [menusResponse, pagesResponse] = await Promise.all([
        getAllMenulist(),
        getAllPagesList(),
      ]);
      setMenusList(menusResponse.data);
      setPagesListData(pagesResponse.data);
    } catch (error) {
      console.error("Error fetching menus or pages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMapPages = async () => {
    if (!selectedMenu || !selectedPage) return;
    try {
      const payload = {
        page_id: selectedPage,
        menu_id: selectedMenu,
      };
      const response = await AddMenuMap(payload);
      if (response.status_code === 200) {
        toast.success("Page mapped successfully");
        handleClose();
        setSelectedMenu(null);
        setSelectedPage(null);
        if (onActionComplete) onActionComplete();
      }
    } catch (error) {
      console.error("Error mapping page:", error);
      toast.error("An error occurred while mapping the page.");
    }
  };

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 1,
          p: 4,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            backgroundColor: "white",
            "&:hover": { backgroundColor: "lightgray" },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>

        <Typography
          variant="h6"
          gutterBottom
          sx={{
            textAlign: "center",
            textTransform: "uppercase",
          }}
        >
          Map model
        </Typography>
        <hr />
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={120}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "grey",
                }}
              >
                Select a Menu
              </Typography>
              <Divider />
              <Paper
                elevation={2}
                sx={{
                  maxHeight: 350,
                  overflowY: "auto",
                  p: 2,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "none",
                  },
                }}
              >
                <RadioGroup
                  value={selectedMenu}
                  onChange={(e) => setSelectedMenu(Number(e.target.value))}
                >
                  {menusList.length > 0 ? (
                    menusList.map((menu) => (
                      <FormControlLabel
                        key={menu.menu_id}
                        value={menu.menu_id}
                        control={<Radio />}
                        label={menu.menu_name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1,
                          borderRadius: 1,
                          transition: "background-color 0.2s",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      />
                    ))
                  ) : (
                    <Typography sx={{ textAlign: "center", color: "gray" }}>
                      No menus available.
                    </Typography>
                  )}
                </RadioGroup>
              </Paper>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "grey",
                }}
              >
                Select a Page
              </Typography>
              <Divider />
              <Paper
                elevation={2}
                sx={{
                  maxHeight: 350,
                  overflowY: "auto",
                  p: 2,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "none",
                  },
                }}
              >
                <RadioGroup
                  value={selectedPage}
                  onChange={(e) => setSelectedPage(Number(e.target.value))}
                >
                  {pagesListData.length > 0 ? (
                    pagesListData.map((page) => (
                      <FormControlLabel
                        key={page.id}
                        value={page.id}
                        control={<Radio />}
                        label={page.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1,
                          borderRadius: 1,
                          transition: "background-color 0.2s",
                          "&:hover": { backgroundColor: "#f0f0f0" },
                        }}
                      />
                    ))
                  ) : (
                    <Typography sx={{ textAlign: "center", color: "gray" }}>
                      No pages available.
                    </Typography>
                  )}
                </RadioGroup>
              </Paper>
            </Grid>
          </Grid>
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: "100%", mt: 4, justifyContent: "center" }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMapPages}
            disabled={!selectedMenu || !selectedPage || loading}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            Map Page
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default MenuMap;
